import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useRevalidator,
} from "react-router-dom";
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import { Nav, Navbar } from "react-bootstrap";
import { APIProvider } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { parseOperator } from "./Data/ApiParse";
import { Facility, Operator, SearchResult, SubscriptionParker } from "./Data/ApiTypes";
import { FacilityListSearch } from "./Controls/FacilityListSearch";
import { SearchEntityType, SubscriptionStatus } from "./Data/ApiTransport";
import ParkerEditModal, { SearchSubParkerContext } from "./ParkerEditModal";
import { toast } from "react-toastify";
import { facilityCache } from "./services/FacilityCache";
import { extractClaims, canPerformAction, getDefaultRedirectPath } from "./helpers/AuthorizationManager";
import { userCanUseFacilityListSearch } from "./helpers/RoleManager";
import { useAuthorization } from "./hooks/useAuthorization";
import {
  ArrowLeftCircle,
  BarChart,
  Buildings,
  Calendar2Heart,
  Calendar2Week,
  CarFrontFill,
  ClockHistory,
  CreditCard,
  CurrencyDollar,
  FileEarmarkBarGraph,
  FileText,
  GraphUp,
  House,
  InboxFill,
  List,
  PCircle,
  People,
  PersonCircle,
  PiggyBank,
  Receipt,
  Ticket,
  X,
} from "react-bootstrap-icons";
import "./AppLoggedIn.css";
import { GroupParkerFinalizeBanner } from "./Controls/GroupParkerFinalizeBanner";

function UserMenu() {
  const nav = useNavigate();
  const [auth, setAuth] = useAtom(authAtom);

  if (!auth.isLoggedIn || !auth.account) return null;

  return (
    <>
      <div className="d-none d-md-flex align-items-center gap-3">
        <Nav.Link
          eventKey="user"
          onClick={() => nav("/user")}
          className="d-flex gap-2 align-items-center text-white"
        >
          {auth.account.emailAddress}
        </Nav.Link>
        <Nav.Link
          eventKey="logout"
          onClick={() => {
            setAuth({
              isLoggedIn: false,
              account: undefined,
              accessToken: undefined,
            });
            nav("/login");
          }}
          className="text-nowrap text-white fs-6"
        >
          Log Out
        </Nav.Link>
      </div>
    </>
  );
}

function SlideOverMenu({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const nav = useNavigate();
  const [auth, setAuth] = useAtom(authAtom);
  const { canPerformAction, canAccessPath } = useAuthorization();

  const [entered, setEntered] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const { facilityId, parkerId } = useParams<{
    facilityId?: string;
    parkerId?: string;
  }>();
  const location = useLocation();

  const deriveContextFromPath = useCallback(():
    | "main"
    | "finance"
    | "parker" => {
    if (location.pathname.includes("/finance")) return "finance";
    if (location.pathname.includes("/parker/") && parkerId) return "parker";
    return "main";
  }, [location.pathname, parkerId]);

  const can = (tab: 'overview'|'listing'|'status'|'parker'|'plan'|'rate'|'finance'|'reports') => {
    if (!facilityId) return false;
    const path = `/facility/${facilityId}/${tab}`;
    return canAccessPath(path);
  };

  const initialContext = deriveContextFromPath();
  const [menuContext, setMenuContext] = useState<"main" | "finance" | "parker">(
    initialContext
  );

  const [visibleContext, setVisibleContext] = useState<
    "main" | "finance" | "parker"
  >(initialContext);

  useEffect(() => {
    const ctx = deriveContextFromPath();
    setMenuContext(ctx);
    if (show) setVisibleContext(ctx);
  }, [deriveContextFromPath, show]);

  useEffect(() => {
    if (show) {
      setVisibleContext(menuContext);
    }
  }, [show, menuContext]);

  useEffect(() => {
    let raf1: number | null = null;
    let raf2: number | null = null;
    let timeoutId: number | null = null;

    if (show) {
      setShouldRender(true);
      setEntered(false);
      raf1 = requestAnimationFrame(() => {
        void panelRef.current?.offsetHeight;
        raf2 = requestAnimationFrame(() => setEntered(true));
      });
    } else {
      setEntered(false);
      timeoutId = window.setTimeout(() => setShouldRender(false), 350);
    }

    return () => {
      if (raf1 !== null) cancelAnimationFrame(raf1);
      if (raf2 !== null) cancelAnimationFrame(raf2);
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [show]);

  const menuItems = useMemo(() => {
    if (visibleContext === "finance" && facilityId) {
      return [
        {
          label: "Main Menu",
          icon: <ArrowLeftCircle />,
          onClick: () => setVisibleContext("main"),
        },
        {
          label: "Overview",
          icon: <PersonCircle />,
          path: `/facility/${facilityId}/finance/overview`,
        },
        {
          label: "Invoices",
          icon: <Receipt />,
          path: `/facility/${facilityId}/finance/invoice`,
        },
        {
          label: "Payouts",
          icon: <PiggyBank />,
          path: `/facility/${facilityId}/finance/payout`,
        },
      ];
    }

    if (visibleContext === "parker" && facilityId && parkerId) {
      return [
        {
          label: "Main Menu",
          icon: <ArrowLeftCircle />,
          onClick: () => setVisibleContext("main"),
        },
        {
          label: "Overview",
          icon: <PersonCircle />,
          path: `/facility/${facilityId}/parker/${parkerId}/overview`,
        },
        {
          label: "Plans",
          icon: <FileText />,
          path: `/facility/${facilityId}/parker/${parkerId}/plans`,
        },
        {
          label: "Reservations",
          icon: <Calendar2Week />,
          path: `/facility/${facilityId}/parker/${parkerId}/reservations`,
        },
        {
          label: "History",
          icon: <ClockHistory />,
          path: `/facility/${facilityId}/parker/${parkerId}/balance/history`,
        },
        {
          label: "Orders",
          icon: <InboxFill />,
          path: `/facility/${facilityId}/parker/${parkerId}/order`,
        },
        {
          label: "Invoices",
          icon: <Receipt />,
          path: `/facility/${facilityId}/parker/${parkerId}/invoice`,
        },
        {
          label: "Payments",
          icon: <CurrencyDollar />,
          path: `/facility/${facilityId}/parker/${parkerId}/payment`,
        },
        {
          label: "Vehicles",
          icon: <CarFrontFill />,
          path: `/facility/${facilityId}/parker/${parkerId}/vehicle`,
        },
      ];
    }

    if (facilityId) {
      const menuItems: Array<{
        label: string;
        subtext?: string;
        icon: JSX.Element;
        path?: string;
        onClick?: () => void;
      }> = [];

      // Only show "Back to Admin" or "Back to Operator" if user has access
      if (canAccessPath('/admin')) {
        menuItems.push({
          label: "Back to Admin",
          icon: <ArrowLeftCircle />,
          path: `/admin/operators`,
          onClick: () => setVisibleContext("main"),
        });
      } else if (canAccessPath('/operator') || canAccessPath('/operator/facilities')) {
        menuItems.push({
          label: "Back to Operator",
          icon: <ArrowLeftCircle />,
          path: `/operator/facilities`,
          onClick: () => setVisibleContext("main"),
        });
      }

      if (can('overview')) {
        menuItems.push({
          label: "Overview",
          icon: <House />,
          path: `/facility/${facilityId}/overview`,
          onClick: () => {
            setMenuContext("main");
            setVisibleContext("main");
          },
        });
      }

      if (can('listing')) {
        menuItems.push({
          label: "Listing",
          icon: <List />,
          path: `/facility/${facilityId}/listing`,
          onClick: () => {
            setMenuContext("main");
            setVisibleContext("main");
          },
        });
      }

      if (can('status')) {
        menuItems.push({
          label: "Transient",
          icon: <PCircle />,
          path: `/facility/${facilityId}/status`,
          onClick: () => {
            setMenuContext("parker");
            setVisibleContext("parker");
          },
        });
      }

      if (can('parker')) {
        menuItems.push({
          label: "Monthly",
          icon: <People />,
          path: `/facility/${facilityId}/parker`,
          onClick: () => {
            setMenuContext("parker");
            setVisibleContext("parker");
          },
        });
      }

      if (can('plan')) {
        menuItems.push({
          label: "Plans",
          subtext: "Monthly",
          icon: <Calendar2Heart />,
          path: `/facility/${facilityId}/plan`,
          onClick: () => {
            setMenuContext("main");
            setVisibleContext("main");
          },
        });
      }

      if (can('rate')) {
        menuItems.push({
          label: "Rates",
          subtext: "Transient",
          icon: <Ticket />,
          path: `/facility/${facilityId}/rate`,
          onClick: () => {
            setMenuContext("main");
            setVisibleContext("main");
          },
        });
      }

      if (can('finance')) {
        menuItems.push({
          label: "Finance",
          icon: <CurrencyDollar />,
          path: `/facility/${facilityId}/finance`,
          onClick: () => {
            setMenuContext("finance");
            setVisibleContext("finance");
          },
        });
      }

      if (can('reports')) {
        menuItems.push({
          label: "Reports",
          icon: <BarChart />,
          path: `/facility/${facilityId}/reports`,
          onClick: () => {
            setMenuContext("main");
            setVisibleContext("main");
          },
        });
      }

      return menuItems;
    }

    // Check for admin access first - supersedes operator access
    if (canAccessPath('/admin') && location.pathname.startsWith('/admin')) {
      return [
        {
          label: "Operators",
          icon: <People />,
          path: "/admin/operators",
          onClick: () => {
            setMenuContext("main");
            setVisibleContext("main");
          },
        },
        {
          label: "Fees",
          icon: <CreditCard />,
          path: "/admin/fees",
          onClick: () => {
            setMenuContext("main");
            setVisibleContext("main");
          },
        },
        {
          label: "Super Admins",
          icon: <People />,
          path: "/admin/users",
          onClick: () => {
            setMenuContext("main");
            setVisibleContext("main");
          },
        },
      ];
    }

    // Build operator menu - each item gated by the appropriate path check.
    // Admin roles use canAccessPath('/operator') (blanket access).
    // FacilityUser is gated per explicit route in ROUTE_PERMISSIONS.
    const operatorItems: Array<{
      label: string;
      subtext?: string;
      icon: JSX.Element;
      path?: string;
      onClick?: () => void;
    }> = [];

    if (canAccessPath('/operator/facilities')) {
      operatorItems.push({
        label: "Facilities",
        icon: <Buildings />,
        path: "/operator/facilities",
        onClick: () => { setMenuContext("main"); setVisibleContext("main"); },
      });
    }

    if (canAccessPath('/operator')) {
      operatorItems.push({
        label: "Dashboard",
        icon: <GraphUp />,
        path: "/operator/dashboard",
        onClick: () => { setMenuContext("main"); setVisibleContext("main"); },
      });
    }

    if (canAccessPath('/operator/reports')) {
      operatorItems.push({
        label: "Reports",
        icon: <FileEarmarkBarGraph />,
        path: "/operator/reports",
        onClick: () => { setMenuContext("main"); setVisibleContext("main"); },
      });
    }

    if (canAccessPath('/operator')) {
      operatorItems.push({
        label: "Staff",
        icon: <People />,
        path: "/operator/staff",
        onClick: () => { setMenuContext("main"); setVisibleContext("main"); },
      });
    }

    if (canAccessPath('/operator')) {
      operatorItems.push({
        label: "Invoices",
        icon: <Receipt />,
        path: "/operator/invoices",
        onClick: () => { setMenuContext("main"); setVisibleContext("main"); },
      });
    }

    if (operatorItems.length > 0) return operatorItems;

    return [];
  }, [visibleContext, facilityId, parkerId]);

  if (!shouldRender) return null;

  return createPortal(
    <>
      <div
        className={`slide-over-overlay ${entered ? "open" : ""}`}
        onClick={onClose}
      />

      <div ref={panelRef} className={`slide-over ${entered ? "open" : ""}`}>
        <div className="slide-over-header d-flex justify-content-between align-items-center p-3">
          <span className="menu-title">Menu</span>
          <button className="btn btn-link text-dark p-0" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="slide-over-body">
          {menuItems.map((item) => (
            <button
              key={item.label + (item.subtext ?? "")}
              onClick={() => {
                if (item.onClick) item.onClick();
                if (item.path) {
                  nav(item.path);
                  onClose();
                }
              }}
              className="slide-over-item"
            >
              {item.icon}
              <span className="slide-over-item-label-wrap">
                <span>{item.label}</span>
                {item.subtext && (
                  <span className="slide-over-item-badge">{item.subtext}</span>
                )}
              </span>
            </button>
          ))}
        </div>

        <div className="slide-over-footer">
          <div
            onClick={() => {
              nav("/user");
              onClose();
            }}
            className="slide-over-item"
            role="button"
          >
            <PersonCircle size={18} />
            Profile
          </div>

          <button
            onClick={() => {
              setAuth({
                isLoggedIn: false,
                account: undefined,
                accessToken: undefined,
              });
              nav("/login");
              onClose();
            }}
            className="logout-btn"
          >
            Log Out
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

function findSearchSubscriptionParker(
  parkers: SubscriptionParker[],
  res: SearchResult,
  mainParkerId: number
): SubscriptionParker | undefined {
  const byId = parkers.find((p) => p.parker.parkerId === res.id);
  if (byId && byId.parker.parkerId !== mainParkerId) {
    return byId;
  }

  const guests = parkers.filter((p) => p.parker.parkerId !== mainParkerId);
  if (guests.length === 1) {
    return guests[0];
  }

  return (
    guests.find((p) =>
      res.name.startsWith(`${p.parker.name} (Parker with`)
    ) ??
    guests.find((p) => res.name === `${p.parker.emailAddress} (Guest)`)
  );
}

async function resolveMainAccountName(
  facilityId: number,
  mainParkerId: number
): Promise<string> {
  const detailsResult = await Api.facilityParkerDetails(
    facilityId,
    mainParkerId
  );
  let name = "this subscription";
  processResult(detailsResult, (details) => {
    name = details.name;
  });
  return name;
}

function TopMenu({
  facility,
  auth,
}: {
  operatorName?: string;
  facility?: Facility;
  auth?: any;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [showParkerEditModal, setShowParkerEditModal] = useState(false);
  const [selectedParker, setSelectedParker] = useState<SubscriptionParker>();
  const [modalParkers, setModalParkers] = useState<SubscriptionParker[]>([]);
  const [selectedParkerSub, setSelectedParkerSub] = useState<{
    subscriptionId: number;
    status: SubscriptionStatus;
    facilityHasIntegrationsEnabled: boolean;
    parkerId: number;
  }>();
  const [modalFacilityId, setModalFacilityId] = useState<number>();
  const [searchSubParkerContext, setSearchSubParkerContext] =
    useState<SearchSubParkerContext>();
  const nav = useNavigate();
  const { revalidate } = useRevalidator();
  const { canPerformAction } = useAuthorization();
  const canManageMonthly = canPerformAction('manage-monthly');
  const facilityName = facility?.name;
  const isOperatorContext = location.pathname.toLowerCase().startsWith("/operator/");
  const userRoles = auth?.account?.roles ?? auth?.roles ?? [];

  function navigateParkerOverview(res: SearchResult) {
    nav(`/facility/${res.facilityId}/parker/${res.id}/overview`);
  }

  function navigateParkerPlan(res: SearchResult, subscriptionId: number) {
    nav(
      `/facility/${res.facilityId}/parker/${res.id}/plan/${subscriptionId}`,
      { state: { from: "search" } }
    );
  }

  async function navigateParkerFromSearch(res: SearchResult) {
    const detailsResult = await Api.facilityParkerDetails(
      res.facilityId,
      res.id
    );
    processResult(
      detailsResult,
      (details) => {
        const activeSubs = details.subscriptions.filter(
          (s) => s.status === SubscriptionStatus.Active
        );
        if (activeSubs.length === 1) {
          navigateParkerPlan(res, activeSubs[0].subscriptionId);
          return;
        }
        navigateParkerOverview(res);
      },
      (err) => {
        toast.error(err);
        navigateParkerOverview(res);
      }
    );
  }

  async function onSelect(res: SearchResult) {
    revalidate();

    if (
      res.type === SearchEntityType.Parker &&
      res.subscriptionId != null
    ) {
      const subResult = await Api.facilitySubscriptionGet(
        res.facilityId,
        res.subscriptionId
      );
      if (!subResult.isOk) {
        toast.error(subResult.error);
        navigateParkerOverview(res);
        return;
      }

      const sub = subResult.value;
      const parker = findSearchSubscriptionParker(sub.parkers, res, sub.parkerId);
      if (!parker) {
        toast.error("Could not load parker for this subscription.");
        navigateParkerOverview(res);
        return;
      }

      const mainAccountName = await resolveMainAccountName(
        res.facilityId,
        sub.parkerId
      );
      setSearchSubParkerContext({
        mainAccountName,
        mainParkerId: sub.parkerId,
        subscriptionId: sub.subscriptionId,
      });

      setSelectedParker(parker);
      setModalParkers(sub.parkers);
      setSelectedParkerSub({
        subscriptionId: sub.subscriptionId,
        status: sub.status,
        facilityHasIntegrationsEnabled: sub.facility.hasIntegrationsEnabled,
        parkerId: sub.parkerId,
      });
      setModalFacilityId(res.facilityId);
      if (!canManageMonthly) {
        nav(`/facility/${res.facilityId}/parker/${sub.parkerId}/plan/${sub.subscriptionId}`);
        return;
      }
      setShowParkerEditModal(true);
      return;
    }

    if (res.type === SearchEntityType.Parker) {
      await navigateParkerFromSearch(res);
      return;
    }

    switch (res.type) {
      case SearchEntityType.Staff:
        alert("Nav to staff not implemented");
        break;
      case SearchEntityType.Vehicle:
        nav(`/facility/${res.facilityId}/parker/${res.id}/vehicle/${res.id}`);
        break;
      case SearchEntityType.Order:
        nav(`/facility/${res.facilityId}/parker/${res.id}/order/${res.id}`);
        break;
      case SearchEntityType.Invoice:
        nav(`/facility/${res.facilityId}/parker/${res.id}/invoice/${res.id}`);
        break;
      case SearchEntityType.Payment:
        nav(`/facility/${res.facilityId}/parker/${res.id}/payment/${res.id}`);
        break;
      case SearchEntityType.AccessKey:
        nav(`/facility/${res.facilityId}/key/${res.id}`);
        break;
      default:
        throw new Error("Entity Type Invalid");
    }
  }

  return (
    <>
      <Navbar
        collapseOnSelect
        className="px-3 px-md-5 gap-0"
        expand="lg"
        bg="black"
        variant="dark"
      >
        <Navbar.Brand
          onClick={() => nav(getDefaultRedirectPath(extractClaims(auth)))}
          style={{ cursor: 'pointer' }}
          className="fs-5 d-flex align-items-center gap-2 flex-shrink-1"
        >
          <img src="/spotsync-logo.svg" style={{ width: "1.5em" }} />
          <span className="d-none d-sm-block">
            <span className="fw-bold">Spot</span>Sync
          </span>
          {facilityName && (
            <span className="d-none d-md-block">{facilityName}</span>
          )}
        </Navbar.Brand>

        {(facilityName || isOperatorContext) && userCanUseFacilityListSearch(userRoles) && (
          <>
            <FacilityListSearch
              className="d-none d-md-block px-0 pe-2 px-sm-2 flex-grow-1 d-print-none"
              facility={facility}
              placeholder="Search name, email, phone, or plate..."
              onSelect={onSelect}
              mode={isOperatorContext ? 'operator' : 'facility'}
            />
            <FacilityListSearch
              className="d-block d-md-none px-0 pe-2 px-sm-2 flex-grow-1 d-print-none"
              facility={facility}
              placeholder={facility?.name || ''}
              onSelect={onSelect}
              mode={isOperatorContext ? 'operator' : 'facility'}
            />
          </>
        )}

        {facilityName && !userCanUseFacilityListSearch(userRoles) && (
          <div className="flex-grow-1"></div>
        )}

        {(!facilityName && !isOperatorContext) && <div className="flex-grow-1"></div>}

        <Nav className="d-none d-md-flex">
          <UserMenu />
        </Nav>

        <div className="d-flex d-md-none">
          <Navbar.Toggle onClick={() => setShowMenu(true)} />
        </div>
      </Navbar>

      <SlideOverMenu show={showMenu} onClose={() => setShowMenu(false)} />

      {selectedParker && selectedParkerSub && modalFacilityId != null && (
        <ParkerEditModal
          show={showParkerEditModal}
          onClose={() => {
            setShowParkerEditModal(false);
            setSearchSubParkerContext(undefined);
          }}
          parker={selectedParker}
          setParker={setSelectedParker}
          parkers={modalParkers}
          setParkers={setModalParkers}
          selectedParkerSub={selectedParkerSub}
          facilityId={modalFacilityId}
          searchSubParkerContext={searchSubParkerContext}
        />
      )}
    </>
  );
}

export function AppLoggedIn() {
  const location = useLocation();
  const nav = useNavigate();
  const [auth] = useAtom(authAtom);
  const facilityIdStr = useParams().facilityId;
  const [operator, setOperator] = useState<Operator>();
  const [facility, setFacility] = useState<Facility>();

  // Guard against unauthorized navigation
  useEffect(() => {
    if (!auth?.isLoggedIn) return;
    
    // Dynamic import to avoid circular dependencies
    import('./helpers/AuthorizationManager').then(({ extractClaims, canAccessPath, getDefaultRedirectPath }) => {
      const claims = extractClaims(auth);
      if (!canAccessPath(claims, location.pathname)) {
        nav(getDefaultRedirectPath(claims), { replace: true });
      }
    }).catch(() => {
      // Fail silently if import fails
    });
  }, [location.pathname, auth, nav]);

  useEffect(() => {
    Api.opDetails().then((res) =>
      processResult(res, (val) => setOperator(parseOperator(val)))
    );
  }, []);

  useEffect(() => {
    if (!facilityIdStr) {
      setFacility(undefined);
      return;
    }

    const facilityId = parseInt(facilityIdStr);

    // Check cache first
    const cachedFacility = facilityCache.getFacility(facilityId);
    if (cachedFacility) {
      setFacility(cachedFacility);
      return;
    }
  }, [facilityIdStr]);

  return (
    <APIProvider
      apiKey="AIzaSyBW7pTkR8xHrxMHXOQWGqXo4C518nbgNgo"
      libraries={["places"]}
    >
      <div id="main">
        <TopMenu operatorName={operator?.name} facility={facility} auth={auth} />
        <div id="content">
          <Outlet />
        </div>
        <GroupParkerFinalizeBanner />
      </div>
    </APIProvider>
  );
}

export default AppLoggedIn;
